import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { InfoDot } from '@/components/ui/InfoDot';
import { formatUsd } from '@/lib/format';
import {
  useAcknowledgeReturn,
  useClearHandledReturns,
  useDismissReturn,
  usePaymentReturns,
  type PaymentReturnRow,
} from '@/hooks/admin/usePaymentCutover';
import { CASE_STATUS_LABEL, useCaseAction, type CaseStatus } from '@/hooks/admin/usePaymentCases';
import {
  ActionsTd,
  Attention,
  PayTable,
  SectionHeader,
  SubTabs,
  TableState,
  Td,
  Th,
} from '@/components/admin/PaymentsLayout';

/**
 * Returned payments — bounced and disputed payments, fed by the Stripe
 * webhooks (historical Bill & Pay rows stay on record). Three views:
 *
 *   Open        nobody has started on it: no ticket, not marked handled
 *   Processing  a ticket is working it (answering the customer, contesting at
 *               Stripe, booked and waiting on the bank's decision)
 *   Closed      marked handled, or its ticket is won / lost / closed
 */

type View = 'open' | 'processing' | 'closed';

const DONE_CASE = new Set(['won', 'lost', 'closed']);

export function returnView(r: PaymentReturnRow): View {
  if (r.case && !DONE_CASE.has(r.case.status)) return 'processing';
  if (r.acknowledged_at || (r.case && DONE_CASE.has(r.case.status))) return 'closed';
  return 'open';
}

function caseLabel(n: number): string {
  return `PC-${String(n).padStart(4, '0')}`;
}

export function ReturnsPanel() {
  const returns = usePaymentReturns();
  const acknowledge = useAcknowledgeReturn();
  const dismissReturn = useDismissReturn();
  const clearHandled = useClearHandledReturns();
  const openTicket = useCaseAction<{ ok: true; id: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('open');

  const rows = returns.data?.rows ?? [];
  const by: Record<View, PaymentReturnRow[]> = { open: [], processing: [], closed: [] };
  for (const r of rows) by[returnView(r)].push(r);
  const shown = by[view];
  const hasHandled = rows.some((r) => r.acknowledged_at);

  return (
    <div className="space-y-3">
      <SectionHeader
        description={
          <span className="flex items-center gap-1.5">
            Bounced and disputed payments, detected automatically. Start a ticket to answer the customer, contest it at
            Stripe and book it in QuickBooks.
            <InfoDot text="Stripe reports failed and disputed charges by webhook; an alert email goes to service@brixbev.com and the payment lands here. Paper checks or wires outside the portal won't appear — catch those from the bank feed and book them from the customer's Billing tab." />
          </span>
        }
        actions={
          <Button
            variant="secondary"
            disabled={!hasHandled || clearHandled.isPending}
            onClick={() => {
              if (!window.confirm('Clear all handled returns out of the list? They stay on record but leave this queue.')) return;
              clearHandled.mutate();
            }}
          >
            {clearHandled.isPending ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
            Clear handled
          </Button>
        }
      />

      <SubTabs
        tabs={[
          { key: 'open', label: 'Open', count: by.open.length },
          { key: 'processing', label: 'Processing', count: by.processing.length },
          { key: 'closed', label: 'Closed', count: by.closed.length },
        ]}
        value={view}
        onChange={setView}
        right={
          <Button variant="secondary" size="sm" onClick={() => returns.refetch()} disabled={returns.isFetching}>
            {returns.isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </Button>
        }
      />

      {view === 'open' && (
        <Attention count={by.open.length}>
          payments came back with no ticket yet. Start a ticket on each to answer the customer and book it.
        </Attention>
      )}

      {openTicket.isError && <p className="text-sm text-red-700">Could not open the ticket: {openTicket.error.message}</p>}

      <TableState
        loading={returns.isLoading}
        error={returns.isError ? returns.error : null}
        empty={shown.length === 0}
        emptyText={
          view === 'open' ? 'Nothing open.' : view === 'processing' ? 'No tickets in progress.' : 'Nothing closed yet.'
        }
      >
        <PayTable minWidth={880}>
          <thead>
            <tr>
              <Th>Returned</Th>
              <Th>Customer</Th>
              <Th right>Amount</Th>
              <Th>Reason</Th>
              <Th>Ticket &amp; books</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brix-border">
            {shown.map((r) => {
              const pending = openTicket.isPending && openTicket.variables?.return_id === r.id;
              return (
                <tr key={r.id}>
                  <Td className="whitespace-nowrap text-xs">{r.returned_at?.slice(0, 10) ?? '—'}</Td>
                  <Td>
                    {r.customer_id ? (
                      <Link to={`/admin/customers/${r.customer_id}/billing`} className="font-semibold text-brix-ink hover:underline">
                        {r.customer_name ?? 'Customer'}
                      </Link>
                    ) : (
                      <span className="font-semibold">{r.customer_name ?? (r.qbo_customer_id ? `QBO #${r.qbo_customer_id}` : '—')}</span>
                    )}
                    {r.payment_id && <span className="block text-[11px] text-brix-muted">portal payment</span>}
                  </Td>
                  <Td right mono>{r.amount != null ? formatUsd(Number(r.amount)) : '—'}</Td>
                  <Td className="text-xs">
                    {r.reason_code && <span className="mr-1 font-mono font-semibold">{r.reason_code}</span>}
                    {r.reason_text ?? ''}
                    {r.payment_failed && <span className="block font-semibold text-red-700">funds withheld</span>}
                  </Td>
                  <Td className="text-xs">
                    {r.case ? (
                      <>
                        <span className="font-semibold">{caseLabel(r.case.case_number)}</span>
                        <span className="text-brix-muted"> · {CASE_STATUS_LABEL[r.case.status as CaseStatus] ?? r.case.status}</span>
                      </>
                    ) : (
                      <span className="text-brix-muted">no ticket</span>
                    )}
                    <span className={r.booked_at ? 'block text-emerald-700' : 'block text-brix-muted'}>
                      {r.booked_at ? 'booked in QuickBooks' : 'not booked'}
                    </span>
                    {r.acknowledged_at && (
                      <span className="block text-brix-muted">handled by {r.acknowledged_by ?? 'admin'}</span>
                    )}
                  </Td>
                  <ActionsTd>
                    {r.case ? (
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/payments/cases/${r.case!.id}`)}>
                        View ticket
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={openTicket.isPending}
                        title="Collect the invoices and delivery proof, draft the email and letter, and book it in QuickBooks"
                        onClick={() =>
                          openTicket.mutate(
                            { action: 'open', return_id: r.id },
                            { onSuccess: (res) => navigate(`/admin/payments/cases/${res.id}`) },
                          )
                        }
                      >
                        {pending && <Loader2 size={12} className="animate-spin" />}
                        Start ticket
                      </Button>
                    )}
                    {!r.acknowledged_at && (
                      <Button variant="secondary" size="sm" disabled={acknowledge.isPending} onClick={() => acknowledge.mutate({ id: r.id })}>
                        Mark handled
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Clear from list (stays on record)"
                      disabled={dismissReturn.isPending}
                      onClick={() => {
                        if (!window.confirm('Clear this return from the list? It stays on record but leaves this queue.')) return;
                        dismissReturn.mutate({ id: r.id });
                      }}
                    >
                      <X size={14} />
                    </Button>
                  </ActionsTd>
                </tr>
              );
            })}
          </tbody>
        </PayTable>
      </TableState>
    </div>
  );
}
